import {
  AddOnPaymentOptions,
  type DepositDetails,
  FrequencyType,
  type Money,
  type Payment,
  RateType,
  type Service,
} from '@wix/ambassador-bookings-services-v2-service/types';
import { ServicePaymentDto as BaseServicePaymentDto } from '@wix/bookings-uou-types';
import { computePercentDeposit } from '../../../payment/deposit/percentDeposit';
import { getSubscriptionDisplayInfo } from '../../../subscription/getSubscriptionDisplayInfo';

export enum OfferedAsType {
  ONE_TIME = 'ONE_TIME',
  PRICING_PLAN = 'PRICING_PLAN',
}

export enum PaymentType {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  BOTH = 'BOTH',
}

export interface MoneyDto {
  currency: string;
  price: number;
  formattedValue: string;
}

export interface SubscriptionDiscount {
  name?: string;
  subscriptionCycles?: number;
}

export interface SubscriptionDiscountDetails {
  discountCalculatedAtCheckout?: boolean;
  price: number;
  priceBeforeDiscount: number;
  // Absent when separate participant items have different calculated prices.
  pricePerParticipant?: number;
  discount: SubscriptionDiscount;
}
export type ParticipantPricing = {
  price: number;
  deposit?: number;
};

export interface ServicePaymentDto extends BaseServicePaymentDto {
  subscriptionDiscountDetails?: SubscriptionDiscountDetails;
  addonsPaymentOptions?: AddOnPaymentOptions;
  priceBeforeDiscount?: number;
  discountRuleName?: string;
  discountCalculatedAtCheckout?: boolean;
  isOrderDiscount?: boolean;
  fullUpfrontPaymentAllowed?: boolean;
  // Percentage deposit only: the configured percent as a decimal string ("20" / "20.5").
  depositPercentage?: string;
  // Keyed by participant id; `price`/`minCharge` cover the whole slot.
  participantPricing?: { [participantId: string]: ParticipantPricing };
  subscriptionInfo?: {
    totalPayments: number;
    frequency: FrequencyType;
    enrollmentFeeAmount?: Money;
  };
}

export interface PricingPlanInfo {
  displayText: string;
  pricingPlans: string[];
}

export interface ServicePayment {
  offeredAs: OfferedAsType[];
  paymentDetails: ServicePaymentDto;
  pricingPlanInfo?: PricingPlanInfo;
}

export interface PaymentOptions {
  custom?: Boolean;
  wixPaidPlan?: Boolean;
  wixPayInPerson?: Boolean;
  wixPayOnline?: Boolean;
}

export const mapServicePayment = (service: Service): ServicePayment => {
  return {
    offeredAs: mapServiceOfferedAsDto(service),
    pricingPlanInfo: mapServicePricingPlansDto(service),
    paymentDetails: mapServicePaymentDto(service),
  };
};
const isPayable = (payment: Payment) => {
  return payment.options?.inPerson || payment.options?.online;
};

export const determinePaymentOptionsBy = (
  payment: Payment,
): OfferedAsType[] => {
  const pricingPlanIds = payment.pricingPlanIds;
  return [
    ...(pricingPlanIds?.length && payment.options?.pricingPlan
      ? [OfferedAsType.PRICING_PLAN]
      : []),
    ...(isPayable(payment) ? [OfferedAsType.ONE_TIME] : []),
  ];
};

export const getPriceText = (payment: Payment) =>
  payment?.rateType === RateType.CUSTOM && payment.custom?.description
    ? payment.custom.description
    : '';

export const mapServicePricingPlansDto = (
  service: Service,
): PricingPlanInfo => {
  const { payment } = service;
  const pricingPlansIds = payment?.pricingPlanIds || [];

  const displayText = getPriceText(payment!);

  return {
    displayText,
    pricingPlans: pricingPlansIds,
  };
};

export function mapServiceOfferedAsDto(service: Service) {
  const payment = service.payment;
  return determinePaymentOptionsBy(payment!);
}

export const isFreeService = (payment: Payment, price: number) => {
  if (payment?.rateType === RateType.SUBSCRIPTION) {
    return false;
  }
  return !!payment?.custom || (!price && !payment?.options?.pricingPlan);
};

export function mapPaymentOptionsToPaymentType(payment: Payment): PaymentType {
  if (payment?.rateType === RateType.SUBSCRIPTION) {
    return PaymentType.ONLINE;
  }
  if (payment.options?.online && !payment.options?.inPerson) {
    return PaymentType.ONLINE;
  }
  if (!payment?.options?.online && payment?.options?.inPerson) {
    return PaymentType.OFFLINE;
  }
  return PaymentType.BOTH;
}

// A percentage deposit lives on `depositDetails.percentage`, and leaves the legacy `deposit` empty —
// so reading `deposit.value` alone gives NaN and every `deposit > 0` gate silently closes. Resolving
// it here is only a seed, so option visibility and the default option are right on the first render;
// calculatePaymentDetails replaces it with the exact per-booking amount.
// An amount deposit is mirrored on `depositDetails.amount`, but the server dual-populates the legacy
// field alongside it, so reading that alone is complete.
const resolveMinCharge = ({
  depositDetails,
  legacyDeposit,
  basePrice,
}: {
  depositDetails?: DepositDetails;
  legacyDeposit?: Money;
  basePrice: number;
}): number =>
  depositDetails?.percentage
    ? computePercentDeposit(basePrice, depositDetails.percentage)
    : Number(legacyDeposit?.value) || 0;

export function mapPaymentFromRate(payment: Payment) {
  const paymentInfo: {
    isFree: boolean;
    priceText: string;
    paymentType: PaymentType;
    currency: string;
    price: number;
    minCharge: number;
    depositPercentage?: string;
    isVariedPricing: boolean;
    addonsPaymentOptions: AddOnPaymentOptions;
  } = {
    isFree: false,
    priceText: getPriceText(payment),
    paymentType: PaymentType.OFFLINE,
    currency: '',
    price: 0,
    minCharge: 0,
    isVariedPricing: false,
    addonsPaymentOptions: payment.addOnOption || AddOnPaymentOptions.IN_PERSON,
  };

  if (payment?.rateType === RateType.FIXED) {
    paymentInfo.currency = payment?.fixed?.price?.currency || '';
    paymentInfo.price = Number(payment?.fixed?.price?.value);
    paymentInfo.depositPercentage =
      payment?.fixed?.depositDetails?.percentage ?? undefined;
    paymentInfo.minCharge = resolveMinCharge({
      depositDetails: payment?.fixed?.depositDetails,
      legacyDeposit: payment?.fixed?.deposit,
      basePrice: paymentInfo.price,
    });
  }

  if (payment?.rateType === RateType.VARIED) {
    paymentInfo.isVariedPricing = true;
    paymentInfo.currency = payment?.varied?.defaultPrice?.currency || '';
    paymentInfo.price = Number(payment.varied?.defaultPrice?.value);
    paymentInfo.depositPercentage =
      payment?.varied?.depositDetails?.percentage ?? undefined;
    // Seeded off minPrice, not defaultPrice, because minPrice is what the pre-selection line shows
    // ("From $50" — see getPriceLineText); a higher defaultPrice would put an absurd-looking share
    // of the price next to it.
    paymentInfo.minCharge = resolveMinCharge({
      depositDetails: payment?.varied?.depositDetails,
      legacyDeposit: payment?.varied?.deposit,
      basePrice: Number(payment?.varied?.minPrice?.value) || paymentInfo.price,
    });
  }

  if (payment?.rateType === RateType.SUBSCRIPTION) {
    const subscriptionAmount = payment.subscription?.amountPerBillingCycle;
    paymentInfo.currency = subscriptionAmount?.currency || '';
    paymentInfo.price = subscriptionAmount?.value
      ? Number(subscriptionAmount.value)
      : 0;
    paymentInfo.minCharge = 0;
  }

  paymentInfo.isFree = isFreeService(payment, paymentInfo.price);
  paymentInfo.price = paymentInfo.isFree ? 0 : paymentInfo.price;

  paymentInfo.paymentType =
    payment && !paymentInfo.isFree
      ? mapPaymentOptionsToPaymentType(payment)
      : PaymentType.OFFLINE;

  return paymentInfo;
}

export function oneTimePricing(payment: Payment): {
  isFree: boolean;
  isVariedPricing?: boolean;
  priceText: string;
  paymentType: PaymentType;
  currency: string;
  price: number;
  deposit: number;
  depositPercentage?: string;
  addonsPaymentOptions: AddOnPaymentOptions;
} {
  const paymentFromRate = mapPaymentFromRate(payment);
  return {
    isFree: paymentFromRate.isFree,
    priceText: paymentFromRate.priceText,
    paymentType: paymentFromRate.paymentType,
    currency: paymentFromRate.currency,
    price: paymentFromRate.price,
    deposit: paymentFromRate.minCharge,
    depositPercentage: paymentFromRate.depositPercentage,
    isVariedPricing: paymentFromRate.isVariedPricing,
    addonsPaymentOptions: paymentFromRate.addonsPaymentOptions,
  };
}

export const mapServiceMoneyDto = (money?: Money): MoneyDto => {
  if (!money) {
    throw new Error('Price is missing');
  }
  return {
    currency: money.currency || '',
    price: Number(money.value),
    formattedValue: money.formattedValue || '',
  };
};

export const getServiceMinAndMaxPrice = (service: Service) => {
  if (!service?.payment) {
    return {};
  }

  if (
    service.payment.rateType === RateType.SUBSCRIPTION &&
    service.payment.subscription?.amountPerBillingCycle
  ) {
    return {
      maxPrice: mapServiceMoneyDto(
        service.payment.subscription.amountPerBillingCycle,
      ),
      minPrice: mapServiceMoneyDto(
        service.payment.subscription.amountPerBillingCycle,
      ),
    };
  }

  if (service.payment.varied) {
    return {
      maxPrice: mapServiceMoneyDto(service.payment.varied.maxPrice),
      minPrice: mapServiceMoneyDto(service.payment.varied.minPrice),
    };
  }
  if (service.payment.fixed) {
    return {
      maxPrice: mapServiceMoneyDto(service.payment.fixed.price),
      minPrice: mapServiceMoneyDto(service.payment.fixed.price),
    };
  }

  return {};
};

const mapSubscriptionInfo = (payment: Payment) => {
  if (payment?.rateType !== RateType.SUBSCRIPTION || !payment.subscription) {
    return undefined;
  }
  const info = getSubscriptionDisplayInfo(payment.subscription);
  if (!info) {
    return undefined;
  }
  return {
    totalPayments: info.totalPayments,
    frequency: info.frequency,
    enrollmentFeeAmount: info.enrollmentFee,
  };
};

export const mapServicePaymentDto = (service: Service): ServicePaymentDto => {
  const {
    isFree,
    priceText,
    paymentType,
    currency,
    price,
    deposit,
    depositPercentage,
    isVariedPricing,
    addonsPaymentOptions,
  } = oneTimePricing(service.payment!);
  const { minPrice, maxPrice } = getServiceMinAndMaxPrice(service);
  return {
    currency,
    price,
    isFree,
    priceText,
    minCharge: deposit,
    depositPercentage,
    paymentType,
    isVariedPricing,
    minPrice,
    maxPrice,
    addonsPaymentOptions,
    discountRuleName: service.payment?.discountInfo?.discountName,
    fullUpfrontPaymentAllowed:
      service.payment?.fixed?.fullUpfrontPaymentAllowed ??
      service.payment?.varied?.fullUpfrontPaymentAllowed ??
      false,
    subscriptionInfo: mapSubscriptionInfo(service.payment!),
  };
};

export const isServiceOfferedAsPricingPlan = (
  paymentDto: ServicePayment,
  isPricingPlanInstalled: boolean,
) =>
  getOfferedAs(paymentDto, isPricingPlanInstalled).indexOf(
    OfferedAsType.PRICING_PLAN,
  ) >= 0;

export const getOfferedAs = (
  paymentDto: ServicePayment,
  isPricingPlanInstalled: boolean,
) => {
  if (
    paymentDto.offeredAs.indexOf(OfferedAsType.ONE_TIME) >= 0 &&
    paymentDto.offeredAs.indexOf(OfferedAsType.PRICING_PLAN) >= 0
  ) {
    if (
      paymentDto.pricingPlanInfo?.pricingPlans.length === 0 ||
      !isPricingPlanInstalled
    ) {
      return [OfferedAsType.ONE_TIME];
    }
  }
  return paymentDto.offeredAs;
};
